import EmailLink from "@/components/atoms/EmailLink";
import Flex from "@/components/atoms/Flex";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import { PaginateCardProps } from "@/interfaces/Card";
import { IFilters } from "@/interfaces/Fetch";
import {
  useDeleteCompanyCourseInstructorMutation,
  useGetCompanyCourseInstructorsQuery,
} from "@/services/companies/courses/instructors";
import {
  faAt,
  faClock,
  faEllipsisVertical,
  faEuro,
  faFileContract,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { DateTime } from "luxon";
import { ElementRef, FC, useEffect, useRef, useState } from "react";
import { Col, Dropdown, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";
import PaginateCard from "../PaginateCard";
import StoreCompanyCourseInstructorInvoiceModal from "../../modals/company/courses/StoreCompanyCourseInstructorInvoiceModal";

const CompanyCourseInstructorsCard: FC<
  PaginateCardProps & IFilters & { id: string; courseId: string }
> = ({ id, courseId, onGetTotal }) => {
  const StoreCompanyCourseInstructorInvoiceModalRef =
    useRef<ElementRef<typeof StoreCompanyCourseInstructorInvoiceModal>>(null);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);

  const [filters, setFilters] = useState<IFilters>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data } = useGetCompanyCourseInstructorsQuery([id, courseId, filters]);
  const [deleteCompanyCourseInstructorAsync] =
    useDeleteCompanyCourseInstructorMutation();

  const onDelete = async (instructorId: string) => {
    toast.promise(
      deleteCompanyCourseInstructorAsync([id, courseId, instructorId]).unwrap(),
      {
        pending: "Suppression du formateur en cours...",
        success: {
          render() {
            DeleteModalRef.current?.showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Le formateur à bien été supprimé 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  useEffect(() => {
    data?.meta && onGetTotal?.(data.meta.total);
  }, [data?.meta]);

  return (
    <PaginateCard meta={data?.meta} filters={filters} setFilters={setFilters}>
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé le fichier qui a pour id : {{id}} ?"
      />
      <StoreCompanyCourseInstructorInvoiceModal
        ref={StoreCompanyCourseInstructorInvoiceModalRef}
        companyId={id}
        courseId={courseId}
      />
      {data?.data.map(
        ({
          id,
          instructorId,
          priceHt,
          contractDate,
          instructor: { fullname, email, phone },
          createdAt,
        }) => (
          <div
            key={id}
            className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm"
          >
            <Row className="flex-between-center">
              <Col xs={10}>
                <Link
                  to={`/instructors/${instructorId}`}
                  className="fw-semi-bold"
                >
                  {fullname}
                </Link>
              </Col>
              <Col xs={"auto"} className="d-flex justify-content-end">
                <Dropdown
                  align="end"
                  className="btn-reveal-trigger d-inline-block"
                >
                  <Dropdown.Toggle split variant="nolina-default" size="sm">
                    <FontAwesomeIcon
                      icon={faEllipsisVertical}
                      className="fs--2"
                    />
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="border py-0">
                    <div className="py-2">
                      <Dropdown.Item
                        onClick={() =>
                          StoreCompanyCourseInstructorInvoiceModalRef.current?.showOrHiddenModal(
                            id
                          )
                        }
                      >
                        Ajouter une facture
                      </Dropdown.Item>
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() =>
                          DeleteModalRef.current?.showOrHiddenModal(id)
                        }
                      >
                        Supprimer
                      </Dropdown.Item>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
            <div className="border-bottom border-lg-0 border-0 my-3"></div>
            <Row className="flex-between-center">
              <Col xs={12} lg={8}>
                {email && <EmailLink email={email} className="h6" />}
                {phone ? (
                  <PhoneCallLink
                    number={phone}
                    className="d-flex align-items-center gap-1 h6 mb-0 text-600"
                  />
                ) : (
                  <div className="d-flex align-items-center gap-1 h6 mb-0">
                    <FontAwesomeIcon icon={faPhone} transform="shrink-3 up-1" />
                    <UnknownBadge />
                  </div>
                )}
                <div className="d-flex align-items-center gap-1 h6 my-2">
                  <FontAwesomeIcon icon={faEuro} transform="shrink-3 up-1" />
                  <span className="text-600">{priceHt} HT / heure</span>
                </div>
                <div className="d-flex align-items-center gap-1 h6 mb-0">
                  <FontAwesomeIcon
                    icon={faFileContract}
                    transform="shrink-3 up-1"
                  />
                  <span className="text-600">
                    {DateTime.fromISO(contractDate).toFormat("dd-MM-yyyy")}
                  </span>
                </div>
              </Col>
              <Col xs={12} lg={"auto"}>
                <div className="border-bottom border-lg-0 border-0 my-lg-0 my-3"></div>
                <Flex direction="column">
                  <h6 className="mb-0 mt-2">
                    <span className="d-flex align-items-center justify-content-end gap-1">
                      <FontAwesomeIcon
                        icon={faClock}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {DateTime.fromISO(createdAt)
                          .setLocale("fr")
                          .toFormat("dd LLL yyyy")}
                      </span>
                    </span>
                  </h6>
                </Flex>
              </Col>
            </Row>
          </div>
        )
      )}
    </PaginateCard>
  );
};

export default CompanyCourseInstructorsCard;
